/**
 * Turns the outcome of an interval edit on the recording chart (noise / AFib:
 * create, delete, reject, restore, resize) into something the reviewer sees.
 *
 * Before project_management#569 the chart fired these mutations and looked
 * at nothing: a rejected request (GraphQL error, `{success:false}`, or the
 * not-found `{error: "true"}` shape) left the chart unchanged with no message
 * — e.g. "Delete" on automatic noise silently did nothing, because the
 * backend refused to delete analysis rows.
 *
 * Same house rule as `reanalyzeFeedback.ts`: **silence is never success** — a
 * missing or unrecognisable payload is an error, not a quiet success.
 */
import type { FeedbackSeverity } from './reanalyzeFeedback';

export type IntervalEditOp =
  | 'createNoise'
  | 'createAFib'
  | 'deleteNoise'
  | 'deleteAFib'
  | 'rejectNoise'
  | 'restoreNoise'
  | 'editInterval';

export interface IntervalEditFeedback {
  severity: FeedbackSeverity;
  message: string;
}

/** The mutation field each op answers in (reject/restore reuse two mutations). */
const FIELD: Record<IntervalEditOp, string> = {
  createNoise: 'createNoise',
  createAFib: 'createAFib',
  deleteNoise: 'deleteNoise',
  deleteAFib: 'deleteAFib',
  rejectNoise: 'deleteNoise',
  restoreNoise: 'editInterval',
  editInterval: 'editInterval',
};

const WHAT: Record<IntervalEditOp, string> = {
  createNoise: 'Adding the noise interval',
  createAFib: 'Adding the AFib interval',
  deleteNoise: 'Deleting the noise interval',
  deleteAFib: 'Deleting the AFib interval',
  rejectNoise: 'Rejecting the automatic noise',
  restoreNoise: 'Restoring the automatic noise',
  editInterval: 'Saving the interval',
};

/** Only these confirm success in a message — the others show on the chart itself. */
const SUCCESS: Partial<Record<IntervalEditOp, string>> = {
  rejectNoise:
    'Automatic noise rejected. It stays marked as rejected, also after a reanalysis.',
  restoreNoise: 'Automatic noise restored.',
};

interface Payload {
  success?: boolean | null;
  error?: string | null;
  noise?: { isValid?: boolean | null } | null;
}

export interface IntervalEditOutcomeInput {
  /** Apollo `data` from the mutation result. */
  data?: Record<string, Payload | null | undefined> | null;
  /** Apollo error (network failure or GraphQL errors), if the call rejected. */
  error?: { message?: string | null } | null;
}

/**
 * `null` = success that needs no message (the refetched chart shows it);
 * otherwise the message to show.
 */
export function interpretIntervalEdit(
  op: IntervalEditOp,
  input: IntervalEditOutcomeInput,
): IntervalEditFeedback | null {
  const failed = (detail?: string | null): IntervalEditFeedback => ({
    severity: 'error',
    message: `${WHAT[op]} failed${detail ? `: ${detail}` : ''}`,
  });

  if (input.error) return failed(input.error.message);

  const payload = input.data?.[FIELD[op]];

  // No payload, or the legacy not-found shape `{error: "true"}` — not a success.
  if (!payload || payload.success !== true) {
    const detail = payload?.error && payload.error !== 'true' ? payload.error : null;

    return failed(detail ?? (payload ? 'the interval was not found or you may not edit it' : 'no result'));
  }

  // A reject that did not leave the row rejected is not what the user asked for.
  if (op === 'rejectNoise' && payload.noise?.isValid !== false) {
    return failed('the server did not mark it rejected');
  }

  const message = SUCCESS[op];

  return message ? { severity: 'success', message } : null;
}
